import type { AppDownloadBannerProps } from '../components/AppDownloadBanner';
import { DAY_MS } from '../utils/appAdoptionStorage';
import { devicePlatform, isInNativeApp } from '../utils/deviceUtils';
import { isAppAdoptionAudience } from '../utils/appAdoptionAudience';
import { useAdoptionPrompt } from './useAdoptionPrompt';
import { STORE_LINKS } from '../constants/storeLinks';

const HIDDEN_UNTIL_KEY = 'app-banner-hidden-until';
// Short after a store tap: he may not have installed. A gps-app connection hides it for good.
const HIDE_AFTER_OPEN_MS = 2 * DAY_MS;
const HIDE_AFTER_DISMISS_MS = 7 * DAY_MS;

interface AppDownloadBannerInput {
  pathname: string;
  email: string | null;
  serviceDeskId: number | null;
  hasMobileApp: boolean;
  isImpersonating: boolean;
  role: string;
  serviceDeskName: string;
}

export const useAppDownloadBanner = ({
  pathname,
  email,
  serviceDeskId,
  hasMobileApp,
  isImpersonating,
  role,
  serviceDeskName,
}: AppDownloadBannerInput): AppDownloadBannerProps | null => {
  const platform = devicePlatform();
  const { dismissed, act, dismiss } = useAdoptionPrompt({
    hiddenKey: HIDDEN_UNTIL_KEY,
    actHideMs: HIDE_AFTER_OPEN_MS,
    dismissHideMs: HIDE_AFTER_DISMISS_MS,
    platform,
    role,
    serviceDeskName,
  });
  const isTaskRoute = pathname.startsWith('/request') || pathname.includes('/forms/');

  const inAudience = isAppAdoptionAudience({ platform, inNativeApp: isInNativeApp(), email, serviceDeskId, hasMobileApp, isImpersonating });

  if (!inAudience || dismissed || isTaskRoute || platform === 'desktop') return null;

  return {
    url: STORE_LINKS[platform],
    onOpen: act('App Download Click'),
    onDismiss: dismiss('App Download Dismiss'),
  };
};
